/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2013, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'./BaseStylePropertyAction',
	'jquery',
	'../EventHelper',
	'../../../../lib/@waca/dashboard-common/dist/ui/interaction/Utils',
	'./GuidelineManager',
	'underscore',
	'text!./ResizeEdges.html',
	'../../../../app/util/ScreenReaderUtil',
	'../../../../app/nls/StringResources',
	'doT',
	'../../../../lib/@waca/core-client/js/core-client/utils/BrowserUtils',
	'../../../../lib/@waca/core-client/js/core-client/i18n/Formatter',
	'./LayoutIndicator',
	'./SizeIndicator',
	'../../../util/PxPercentUtil',
	'bspopover'
], function(
	BaseClass,
	$,
	eventHelper,
	utils,
	GuidelineManager,
	_,
	resizeEdges,
	ScreenReaderUtil,
	stringResources,
	dot,
	BrowserUtils,
	Formatter,
	LayoutIndicator,
	SizeIndicator,
	PxPercentUtil
) {

	const ABSOLUTE_LAYOUT_CONTENT_SELECTORS = '.pageabsolute>*, .pagegroup>*, .pagetemplateDropZone>*, .pagescalingAbsolute>*, .pagegenericPage>*';

	// Selectors that support resizing the content
	const RESIZE_CONTENT_SELECTOR = '.pagegroup';

	/**
	 * Minimum size allowed for resize per widget
	 * For other widgets, add the spec as shown below. Other widget ids are:
	 * 			"dashboard/widgets/staticwidget/ImageWidget"
	 * 			“dashboard/widgets/staticwidget/MediaWidget”
	 * 			“dashboard/widgets/staticwidget/WebpageWidget”
	 * 			"dashboard/widgets/staticwidget/ShapeWidget"
	 * 			“dashboard-analytics/widgets/livewidget/LiveWidget”
	 */
	const WIDGET_SIZES = {
		'dashboard-core/js/dashboard/widgets/staticwidget/TextWidget': {
			minWidth: 100,
			minHeight: 40
		},
		default: {
			minWidth: 25,
			minHeight: 25
		}
	};

	const Resize = BaseClass.extend({
		sizeIndicators: [],
		containerOffset: 0,

		init: function(controller) {
			Resize.inherited('init', this, arguments);
			this.dashboardApi = controller.dashboardApi;
			this.canvas = this.dashboardApi.getFeature('Canvas');
			this._isTouch = false;
			this.resizingEvents = [];
			this._guidelineManager = new GuidelineManager({ 'layoutController': controller.layoutController });
			this._ScreenReader = new ScreenReaderUtil();
			this._observerUpdateDelay = null;
			this._observer = new window.MutationObserver(this.rotationMutationObserver.bind(this));
			this._focusModeDisabled = controller.config && controller.config.focusModeDisabled;
			const featureChecker = this.dashboard && this.dashboard.getGlassCoreSvc && this.dashboard.getGlassCoreSvc('.FeatureChecker');
			this.isPropertiesV2Enabled = featureChecker && featureChecker.checkValue('dashboard', 'PropertiesV2', 'enabled');
		},

		destroy: function() {
			this._ScreenReader.destroy();
			Resize.inherited('destroy', this, arguments);
		},

		addPositionIndicator: function(){
			this._layoutIndicator.render();
		},

		updatePositionIndicator: function(event, coords){
			var content = '';
			this._layoutIndicator.updatePosition(event);
			content = stringResources.get('widthUnit')+': '  + coords.width + '   ' + stringResources.get('heightUnit')+': ' + coords.height;
			this._layoutIndicator.updateContent(content);
		},

		updatePositionIndicatorRotate: function(event, degrees){

			if(degrees < 0){
				degrees = degrees + 360;
			}
			var content = '';
			this._layoutIndicator.updatePosition(event);
			content = Formatter.formatNumber(String(degrees.toFixed(1))) +  '˚';
			this._layoutIndicator.updateContent(content);
		},

		removePositionIndicator: function(){
			this._layoutIndicator.remove();
		},

		/**
		 * Called by the interaction manager when there is a new selection
		 *
		 * @param nodes
		 * @param evt
		 *            (optional)
		 */
		newSelection: function(nodes) {

			this.resizeStopEvent();

			if (this.selectedNodes) {
				this.removeResizeBorders(this.selectedNodes);
				this._destroyResizeIndicators();
				if(this._layoutIndicator){
					this._layoutIndicator.remove();
				}
			}
			this.selectedNodes = nodes;

			// set up keyboard navigation
			if (nodes.length > 0) {
				this.controller.$el.on('keydown.interactionResizeKey', this.resizeRotateKeyPress.bind(this));
			} else {
				this.controller.$el.off('keydown.interactionResizeKey');
			}

			this.addResizeBorders(nodes);
			this.addResizeIndicators(nodes);
		},

		/**
		 * Resize and rotate keypress handlers
		 */
		_iKeyPressTimer: null,
		resizeRotateKeyPress: function(evt) {
			// only process a keyboard resize/rotate if the shift key is used
			if (!evt.shiftKey || (evt.target && evt.target.className === 'inlineText')) {
				return;
			}

			let resizeNodes = this._getNodesForResize();

			if (evt.altKey) {
				resizeNodes = resizeNodes.filter((node) => !this.preventRotate(node));
				if(!resizeNodes.length) {
					return;
				}
			}

			// determine the width and height deltas
			var iDeltaWidth = 0;
			var iDeltaHeight = 0;
			var iDeltaRotate = 0;
			var sResizeMessage = '';
			var sRotateMessage = '';
			switch (evt.keyCode) {
				case 37:
					iDeltaWidth = -1;
					iDeltaRotate = -1;
					sResizeMessage = stringResources.get('srWidgetResizeSmallerH');
					sRotateMessage = stringResources.get('srWidgetRotateCCW');
					break;
				case 38:
					iDeltaHeight = -1;
					iDeltaRotate = -1;
					sResizeMessage = stringResources.get('srWidgetResizeSmallerV');
					sRotateMessage = stringResources.get('srWidgetRotateCCW');
					break;
				case 39:
					iDeltaWidth = 1;
					iDeltaRotate = 1;
					sResizeMessage = stringResources.get('srWidgetResizeBiggerH');
					sRotateMessage = stringResources.get('srWidgetRotateCW');
					break;
				case 40:
					iDeltaHeight = 1;
					iDeltaRotate = 1;
					sResizeMessage = stringResources.get('srWidgetResizeBiggerV');
					sRotateMessage = stringResources.get('srWidgetRotateCW');
					break;
				default:
					return;
			}

			// broadcast that a resize/rotate has begun
			this.emit('resize:start', {
				'currentSelection': this.selectedNodes
			});

			// perform resize/rotate
			var iNewWidth, iNewHeight, iNewAngle;
			var oStyle = {};
			var modelInfoArray = [];
			var sMessage = '';


			resizeNodes.forEach((n) => {
				// if alt key is used then perform rotate
				var newDeltaWidth = 0;
				var newDeltaHeight =0;


				if (evt.altKey) {

					// rotate
					iNewAngle = Math.round(utils.getAngleDegree(n) + iDeltaRotate);
					oStyle = {
						rotateAngle: iNewAngle
					};

					// perform rotation in the UI
					utils.setRotateTransform(n, iNewAngle);

					// set message
					sMessage = sRotateMessage;
				} else {

					// do a proportional resize if ctrl is held
					if (evt.ctrlKey) {

						// adjust either the width or height to be the same as the other non-zero
						iDeltaWidth = iDeltaWidth || iDeltaHeight;
						iDeltaHeight = iDeltaWidth;

						// update message
						sResizeMessage = stringResources.get(iDeltaWidth > 0 ? 'srWidgetResizeBigger' : 'srWidgetResizeSmaller');
					}

					const containerView = this._getParentGenericPage(n.id);
					var dragbox = { 'top' : n.offsetTop, 'left' :  n.offsetLeft, 'right' : n.offsetLeft + n.clientWidth, 'bottom' : n.offsetTop + n.clientHeight };
					this._guidelineManager.getReady(containerView.domNode, dragbox, true);

					var newSnapCoords = this._guidelineManager.getSnapCoordinates(dragbox);
					var revisedOffsets = {};

					if(this._guidelineManager.gridGuidelines.snapGrid){
						revisedOffsets.x = this._guidelineManager.gridGuidelines.gridSize - newSnapCoords.right;
						revisedOffsets.y = this._guidelineManager.gridGuidelines.gridSize - newSnapCoords.bottom;

						if(revisedOffsets.x < 0.5){
							revisedOffsets.x = 0;
						}

						if(revisedOffsets.y < 0.5){
							revisedOffsets.y = 0;
						}

						revisedOffsets.x = (revisedOffsets.x === null || revisedOffsets.x === 0) ? this._guidelineManager.gridGuidelines.gridSize : revisedOffsets.x;
						revisedOffsets.y = (revisedOffsets.y === null || revisedOffsets.y === 0) ? this._guidelineManager.gridGuidelines.gridSize : revisedOffsets.y;

						newDeltaWidth = iDeltaWidth * revisedOffsets.x;
						newDeltaHeight = iDeltaHeight * revisedOffsets.y;

					}else{
						newDeltaWidth = iDeltaWidth;
						newDeltaHeight = iDeltaHeight;
					}

					// calculate new width and height
					var size = utils.widgetSize(n);
					iNewWidth = size.width + newDeltaWidth;
					iNewHeight = size.height + newDeltaHeight;

					// watch for min size
					if (iNewWidth < WIDGET_SIZES.default.minWidth || iNewHeight < WIDGET_SIZES.default.minHeight) {
						// minimum has been reached so abort
						return;
					}

					// set style object for the model
					oStyle = {
						width: iNewWidth + 'px',
						height: iNewHeight + 'px'
					};

					// set width and height in the UI
					n.style.width = iNewWidth + 'px';
					n.style.height = iNewHeight + 'px';

					// set message
					sMessage = sResizeMessage;
				}

				// if layout then update model
				if (n._layout) {

					// setup position info array to pass to model
					modelInfoArray.push({
						style: oStyle,
						id: n._layout.model.id
					});
				}
			});

			// delay the update of the model to account for multiple keypress events in a row
			if (this._iKeyPressTimer) {
				clearTimeout(this._iKeyPressTimer);
			}
			this._iKeyPressTimer = setTimeout((function() {

				const transactionApi = this.dashboardApi.getFeature('Transaction');
				const transactionToken = transactionApi.startTransaction();

				this.updateModel(modelInfoArray, transactionToken);
				this.refreshProperties();

				this.emit('resize:done', {
					'currentSelection': resizeNodes,
					undoRedoTransactionId: transactionToken.transactionId,
					transactionToken: transactionToken
				});

				transactionApi.endTransaction(transactionToken);

				// update screenreader input
				this._ScreenReader.callOut(sMessage);

			}).bind(this), 500);

			// return false to prevent browser interaction
			return false;
		},

		preventRotate: (node) => $(node).hasClass('noRotate'),

		/**
		 * Add the decoration to the selected items to allow resizing, rotating and scaling.
		 *
		 * @param nodes
		 */
		addResizeBorders: function(nodes) {
			const template = dot.template(resizeEdges);

			_.each(nodes, (n) => {
				const $n = $(n);

				const sResizeHtml = template({
					'rotateHandle': !this.preventRotate(n),
					'isMobile': BrowserUtils.isIPad(),
					'focusModeDisabled': this._focusModeDisabled,
					hasSelectionBox: !n.classList.contains('shapesLine')
				});

				$n.append(sResizeHtml);

				$n.find('.resizeHandle').attr('title', stringResources.get('resize'));
				$n.find('.rotateHandle').attr('title', stringResources.get('rotate'));

				this.addRotationClass(n);
				this.attachEventHandlers(n);
			});
		},

		_showResizeIndicators: function(n, positionDragBox){
			if(n.getBoundingClientRect) {
				this.sizeIndicators.forEach(indicator =>{
					if(indicator.node.id === n.id){
						let bounds = n.getBoundingClientRect();
						indicator.height.updatePosition(bounds, this.containerNodeOffset, true);
						indicator.height.updateContent(bounds.height, positionDragBox.height);
						indicator.height.show();
						indicator.width.updatePosition(bounds, this.containerNodeOffset, false);
						indicator.width.updateContent(bounds.width, positionDragBox.width);
						indicator.width.show();
					}
				});
			}
		},

		_destroyResizeIndicators: function(){
			this.sizeIndicators.forEach(indicator =>{
				indicator.height.remove();
				indicator.width.remove();
			});
		},

		_hideResizeIndicators: function(){
			this.sizeIndicators.forEach(indicator =>{
				indicator.height.hide();
				indicator.width.hide();
			});
		},

		addResizeIndicators: function(nodes){
			if(Array.isArray(nodes)){
				nodes.forEach(node => {
					const containerView = this._getParentGenericPage(node.id);
					let height = new SizeIndicator({ 'node': containerView.$el });
					let width = new SizeIndicator({ 'node': containerView.$el });
					height.render();
					width.render();
					height.hide();
					width.hide();
					this.sizeIndicators.push({ 'node': node, 'height': height, 'width': width });
				});
			}
		},

		/**
		 * Remove the resize borders added to the nodes in the given array
		 *
		 * @param nodes
		 */
		removeResizeBorders: function(nodes) {
			this._observer.disconnect();
			if (this._observerUpdateDelay) {
				window.clearTimeout(this._observerUpdateDelay);
			}
			var i, iLen;
			for (i = 0, iLen = nodes.length; i < iLen; i++) {
				nodes[i]._isTopResizeParent = null;
			}
			$(nodes).removeClass('resize').children('.resizeEdge:not(.resizeEdgeBorder)').remove();
			this.removeRotationClass(nodes);
		},

		/**
		 * Attach the event handlers that will start the resizing operation
		 *
		 * @param node
		 */
		attachEventHandlers: function(node) {
			this._observer.observe(node, { attributes: true });
			var edges = $(node).children('.resizeEdge:not(.resizeEdgeBorder)');
			var resizeStartEvent = this.resizeStartEvent.bind(this);
			for (var i = 0; i < edges.length; i++) {
				eventHelper.on(edges[i], 'touchstart', resizeStartEvent);
				eventHelper.on(edges[i], 'mousedown', resizeStartEvent);
				eventHelper.on(edges[i], 'tap', this.onTap.bind(this));
			}
		},


		/**
		 * Handle the tap event on the resize edges
		 *
		 * The resize edges have a hidden area on touch devices that might block other elements underneath it.
		 * If we are tapping, we might be interacting with another element underneath the resize edge,
		 * so we forward the event to the next element at the tap coordinate
		 *
		 * @param event
		 */
		onTap: function(event) {

			$(event.currentTarget).hide();
			var element = document.elementFromPoint(event.clientX, event.clientY);
			$(event.currentTarget).show();
			if (element !== event.currentTarget) {
				$(element).trigger(event);
			}
		},
		/**
		 * event handler called when we start the resizing operation
		 *
		 * @param evt
		 * @returns {Boolean}
		 */
		resizeStartEvent: function(evt) {
			if (this.isResizeMouseGesture(evt)) {
				this.lastClientX = evt.clientX;
				this.lastClientY = evt.clientY;

				const containerView = this._getParentGenericPage(evt.currentTarget.parentNode.id);
				this.containerNodeOffset = containerView.$el.offset();

				this.initiateResizing(evt);
				evt.stopPropagation();
				evt.preventDefault();
			}
		},

		isResizeMouseGesture: function(evt) {
			var isTouch = this._isTouch && !evt.isTouch;
			var isRightClickOrCtrl = evt.type === 'mousedown' && ((evt.buttons || evt.which) !== 1 || evt.ctrlKey);

			return !isTouch && !isRightClickOrCtrl;
		},


		/**
		 * Prepares the resize operation. Attach all needed event handlers to handler the operation
		 *
		 * @param evt
		 */
		initiateResizing: function(evt) {
			this.lastClientX = evt.clientX;
			this.lastClientY = evt.clientY;
			this._isResizing = true;
			this._isTouch = evt.isTouch;
			this.nResizeEdge = evt.currentTarget;
			this.resizePointCenter = utils.getCenter(this.nResizeEdge);
			this.setupResizingEventHandlers();
			this.nodeAngle = utils.getAngleDegree(this.nResizeEdge.parentNode);

			$('.popover').popover('hide');

			var nodes = this._getNodesForResize();

			var n;
			for (var i = 0; i < nodes.length; i++) {

				n = nodes[i];

				if(nodes.length>=1){
					const containerView = this._getParentGenericPage(n.id);
					this._layoutIndicator = new LayoutIndicator({ 'node': containerView.$el });
					this.addPositionIndicator();
					this._layoutIndicator.hide();
				}

				$(n).addClass('nodeResizing');
				this.saveOldValues(n);
			}
			this.lastBox = this._getDragBox(nodes);

			this.emit('resize:start', {
				'currentSelection': this.selectedNodes
			});
		},

		/**
		* call to redraw the resize borders
		*/
		redrawResizeBorders: function(payload) {
			payload = payload || {};
			if (payload.nodes) {
				this.removeResizeBorders(payload.nodes);
				this.addResizeBorders(payload.nodes);

			}
		},

		/**
		 * Find the closest parent that has scrollbar enabled.
		 *
		 */
		_findScrollableParent: function(parentLayout) {
			var node = null;

			if (parentLayout) {
				var overflowCss = $(parentLayout.domNode).css('overflow');
				if (overflowCss === 'auto' || overflowCss === 'scroll') {
					node = parentLayout.domNode;
				} else {
					node = this._findScrollableParent(parentLayout.parentLayout);
				}
			}

			return node;

		},


		/*
		 * Return info about the current scroll state of the container, sized according to scrollbar visibility
		 */
		_getScrollInfo: function() {
			var scrollInfo = null;

			var scrollableParent = this._findScrollableParent(this._getParentLayout());
			if (scrollableParent) {
				var $node = $(scrollableParent);
				var height = $node.height();
				var width = $node.width();

				var offset = $node.offset();
				scrollInfo = {
					left: offset.left,
					top: offset.top,
					position: $node.position(),
					h: height,
					w: width,
					scrollTop: $node.scrollTop(),
					scrollLeft: $node.scrollLeft(),
					$node: $node
				};
			}

			return scrollInfo;

		},

		/**
		 *  Return the list of nodes that will be used to apply the resize operation
		 * @returns
		 */
		_getNodesForResize: function() {
			if (this.nResizeEdge && this.nResizeEdge.parentNode && this.nResizeEdge.parentNode._isTopResizeParent) {
				return [this.nResizeEdge.parentNode];
			}

			return this.selectedNodes.filter(function(value) {
				return !(value._isTopResizeParent);
			});
		},

		_getParentGenericPage: function(id) {
			//Get the children of the top parent item to get the template box.
			const containerModel = this.controller.layout.findTopLevelParentItem(id);
			let genericPages = containerModel.type === 'genericPage' ? [containerModel] : containerModel.findDescendantsWithType('genericPage');
			return genericPages.length ? this.controller.layoutController.getLayoutView(genericPages[0].id) : null;
		},

		/**
		 * Helper function to save the old values associated with a node before we resize.
		 *
		 * @param n
		 */
		saveOldValues: function(n) {
			var topLeft = utils.position(n);
			var widgetSize = utils.widgetSize(n);
			n._oldValues = {
				'height': widgetSize.height,
				'width': widgetSize.width,
				'top': topLeft.top,
				'left': topLeft.left,
				'rotateAngle': utils.getAngleDegree(n)
			};

			if ($(n).is(RESIZE_CONTENT_SELECTOR)) {
				var children = utils.getContentWithoutDecoration(n);
				for (var i = 0, iLen = children.length; i < iLen; i++) {
					this.saveOldValues(children[i]);
				}
			}
		},

		/**
		 * Attach the event handlers needed by the resize operation after it has started
		 */
		setupResizingEventHandlers: function() {
			var resizeMoveEvent = this.resizeMoveEvent.bind(this);
			var resizeStopEvent = this.resizeStopEvent.bind(this);
			this.resizingEvents.push(
				eventHelper.on(window, 'touchmove', resizeMoveEvent),
				eventHelper.on(window, 'touchend', resizeStopEvent),
				eventHelper.on(window, 'mousemove', resizeMoveEvent),
				eventHelper.on(window, 'mouseup', resizeStopEvent)
			);
		},

		/**
		 * Clear the event handlers that were attached after a resizing operation has started
		 */
		clearResizingEventHandlers: function() {
			utils.removeEvents(this.resizingEvents);
			this.resizingEvents = [];
		},

		/**
		 * Event handler called when the resize operation finishes (e.g. touchend, mouseup)
		 */
		resizeStopEvent: function() {
			this._guidelineManager.finish();
			if (this._isResizing) {
				if(this._layoutIndicator){
					this._layoutIndicator.hide();
				}
				if(this.sizeIndicators){
					this._hideResizeIndicators();
				}
				// Get the nodes being resized before clearing the state
				var nodes = this._getNodesForResize();

				// Clear the resize handler state
				this.lastPageX = null;
				this.lastPageY = null;
				this._isResizing = false;
				this.last = 0;
				this.nodeAngle = null;
				this.nResizeEdge = null;
				this.resizePointCenter = null;
				this.clearResizingEventHandlers();

				var n;
				var styleUpdateArray = [];
				for (let i = 0, iLen = nodes.length; i < iLen; i++) {
					n = nodes[i];
					this._addNodeStyleUpdates(n, styleUpdateArray);
				}
				const transactionApi = this.dashboardApi.getFeature('Transaction');
				const transactionToken = transactionApi.startTransaction();

				this.updateModel(styleUpdateArray, transactionToken);

				const resizedContentList = nodes.map(node => this.canvas.getContent(node.id));
				this.refreshProperties(resizedContentList);

				for (let i = 0, iLen = nodes.length; i < iLen; i++) {
					n = nodes[i];
					n._oldValues = null;
					n._offsetX = null;
					n._offsetY = null;
					this._cleanChildNodes(n);
					$(n).removeClass('nodeResizing nodeResizeMove');
				}
				this.emit('resize:done', {
					'currentSelection': nodes,
					undoRedoTransactionId: transactionToken.transactionId,
					transactionToken: transactionToken
				});

				transactionApi.endTransaction(transactionToken);
			}
		},

		/**
		 * Clean variables that we attached to the child nodes
		 *
		 * @param n
		 */
		_cleanChildNodes: function(n) {
			if ($(n).is(RESIZE_CONTENT_SELECTOR)) {
				var children = utils.getContentWithoutDecoration(n);
				for (var j = 0, jLen = children.length; j < jLen; j++) {
					children[j]._oldValues = null;
				}
			}
		},
		/**
		 * Gather the style updates and add them to the style update map
		 * @param n
		 * @param styleUpdates
		 */
		_addNodeStyleUpdates: function(n, styleUpdateArray) {
			if (n._layout && n._layout.model) {
				var updates = {
					id: n._layout.model.id,
					style: {}
				};

				updates.style = _.extend(utils.position(n), utils.widgetSize(n));
				Object.keys(updates.style).map(key => updates.style[key] += 'px');

				const rotateAngle = utils.getAngleDegree(n);
				if(n._oldValues['rotateAngle'] !== rotateAngle) {
					updates.style['rotateAngle'] = rotateAngle;
				}

				styleUpdateArray.push(updates);
			}
		},


		/**
		 * If we are resizing past the screen size, the mouse position will not increase.
		 * This method will adjust the clientY so that we can resize past the screen
		 */
		adjustYOrdinate: function(e, scrollInfo) {
			var yDelta = this.lastClientY - e.clientY ; // determine direction of movement
			this.lastClientY = e.clientY; // store current position for next time through

			var visBottom = scrollInfo.top + scrollInfo.h;
			if (e.clientY >= visBottom) { //pointer is below the bottom of the container
				if (yDelta <= 0) {
					yDelta = 3; // simulate mouse movement.

					// base new Y off of current box bottom only after it's size has exceeded visible size
					// get the current bottom of the sizing rectangle
					e.clientY = scrollInfo.top + this.lastBox.bottom - 1; // murtest
				}

				// boost the ordinate only when moving down
				e.clientY += yDelta; // virtual ordinate to force downscroll and permit scrolling resize
			}
			return yDelta;
		},

		/**
		 * If we are resizing past the screen size, the mouse position will not increase.
		 * This method will adjust the clientX so that we can resize past the screen
		 */
		adjustXOrdinate: function(e, scrollInfo) {
			var xDelta = this.lastClientX - e.clientX; // determine direction of movement
			this.lastClientX = e.clientX;
			var visRight = scrollInfo.left + scrollInfo.w;

			if (e.clientX > visRight) { //pointer is is right of the right edge of the container
				if (xDelta <= 0) {
					// Simulate mouse movement
					xDelta = 3;

					// boost the ordinate only when moving right
					// get the current sizing rectangle size
					e.clientX = scrollInfo.left + this.lastBox.right; // murtest

				}
				// boost the ordinate only when moving down
				e.clientX += xDelta; // virtual ordinate to force right expand
			}
		},

		_canResize: function(evt) {
			return this.lastClientX !== evt.clientX || this.lastClientY !== evt.clientY;
		},

		/**
		 * Event handler called when there is a resize operation
		 *
		 * @param e
		 * @returns {Boolean}
		 */
		resizeMoveEvent: function(e) {
			if (this._canResize(e)) {
				var scrollInfo = this._getScrollInfo();

				if (scrollInfo) {
					// adjust the Y ordinate if the pointer is outside of container
					this.adjustYOrdinate(e, scrollInfo);

					// adjust the X ordinate if the pointer is outside of container
					this.adjustXOrdinate(e, scrollInfo);
				}


				// do the resize
				this.applyResize(e);
				// store the current sizing rectangle dimensions for next time through
				var nodes = this._getNodesForResize();

				var n;

				for (var i = 0, iLen = nodes.length; i < iLen; i++) {
					n = nodes[i];
					$(n).addClass('nodeResizeMove');
				}

				var dragBox = this._getDragBox(nodes);
				// keep it for next time
				this.lastBox = dragBox;

				// keep track of the last coordinate
				this.lastClientX = e.clientX;
				this.lastClientY = e.clientY;
			}

			return false;
		},

		applyResize: function(e) {
			if(this.nResizeEdge) {
				var $edge = $(this.nResizeEdge);
				var edgeType = this.nResizeEdge.getAttribute('edge');
				var isMobile = BrowserUtils.isIPad();
				var corners = ['br', 'tr', 'bl', 'tl'];

				if((corners.indexOf(edgeType) > -1 && e.shiftKey) || (isMobile && $edge.is('.bottomRight'))) {
					// on desktop, scale on corner edges when user presses shift
					// on mobile, scale when user drags bottom-right scale handle
					this.scale(e, edgeType);
				} else if($edge.is('.rotateEdge') || (isMobile && $edge.is('.bottomLeft')))  {
					// on desktop, rotate on when user hovers near corners then drags
					// on mobile, rotate when user drags bottom-left rotate handle
					this.rotate(e);
				} else {
					this.resizeFromEdge(e);
				}
			}
		},

		_getDragBox: function(nodeInfoList) {
			var dragBox = nodeInfoList.reduce(
				function(box, current) {
					var rect = current.getBoundingClientRect();
					box.top = Math.min(box.top, rect.top);
					box.left = Math.min(box.left, rect.left);
					box.bottom = Math.max(box.bottom, rect.bottom);
					box.right = Math.max(box.right, rect.right);
					box.ids.push(current.id);
					return box;
				}, {
					top: Infinity,
					left: Infinity,
					bottom: 0,
					right: 0,
					ids: []
				}
			);
			return dragBox;
		},

		/**
		 * Resize using the selected resize edge
		 *
		 * @param e
		 */
		resizeFromEdge: function(e) {
			var node = this.nResizeEdge.parentNode;
			var topLeft = utils.position(node);
			var edgeCenterCoords = utils.getRotatedCoordinates(node, this.resizePointCenter.left, this.resizePointCenter.top);
			var resizeCoords = utils.getRotatedCoordinates(node, e.clientX, e.clientY);
			this.resizeSelection(edgeCenterCoords, resizeCoords, topLeft, e);
		},

		/**
		 * Finds the Layout Container for the node being resized.
		 */
		_getParentLayout: function() {
			if (this.nResizeEdge && this.nResizeEdge.parentNode && this.nResizeEdge.parentNode._layout && this.nResizeEdge.parentNode._layout.parentLayout) {
				return this.nResizeEdge.parentNode._layout.parentLayout;
			}
			return null;
		},

		resizeSelection: function(edgeCenterCoords, resizeCoords, topLeft, e) {
			var type = this.nResizeEdge.getAttribute('edge');
			var h = null;
			var w = null;
			var parentLayout = this._getParentLayout();

			if (parentLayout && parentLayout.domNode !== this._guidelineManager.domNode) {
				var ids = this._getNodesForResize().map(
					function(n) {
						return n.id;
					}
				);
				this._guidelineManager.getReady(parentLayout.domNode, {
					dragBox: {
						ids: ids
					}
				});
			}
			this._guidelineManager.hideAll();

			var dragBox = { 'top': (topLeft.top + resizeCoords.y ), 'left': (topLeft.left + resizeCoords.x), 'bottom':  (topLeft.top + resizeCoords.y ),'right': (topLeft.left + resizeCoords.x) };

			var revisedCoordinate = this._guidelineManager.getSnapCoordinates(dragBox);

			if (type.indexOf('b') !== -1) {
				h = resizeCoords.y - edgeCenterCoords.y - revisedCoordinate.bottom;
			} else if (type.indexOf('t') !== -1) {
				h =  edgeCenterCoords.y - (((topLeft.top + resizeCoords.y)-revisedCoordinate.top) -topLeft.top);
			}

			if (type.indexOf('r') !== -1) {
				w = resizeCoords.x - edgeCenterCoords.x - revisedCoordinate.right;
			} else if (type.indexOf('l') !== -1) {
				w = edgeCenterCoords.x - (((topLeft.left + resizeCoords.x)-revisedCoordinate.left) -topLeft.left);
			}

			this.addHeightWidth(h, w, type, e);
		},

		/**
		 * Return the coordinates of the corner opposite to the resize point
		 * The coordinates are relative the axis where the center and rotation are the parent's center and rotation
		 */
		getOppositeCornerCoords: function(node) {
			var type = this.nResizeEdge.getAttribute('edge');
			var oppositeCorners = {
				't': '.br',
				'tl': '.br',
				'r': '.bl',
				'tr': '.bl',
				'b': '.tl',
				'br': '.tl',
				'l': '.tr',
				'bl': '.tr'
			};
			var corner = oppositeCorners[type];
			if (corner) {
				corner = $(node).children(corner)[0];
			}

			var rect = corner ? corner.getBoundingClientRect() : utils.getCenter(node);
			return utils.getRotatedCoordinates(node.parentNode, rect.left, rect.top);
		},


		/**
		 * Adjust the height and width
		 *
		 * @param height - height offset
		 * @param width - width offset
		 */
		addHeightWidth: function(height, width, type, e) {
			var nodes = this._getNodesForResize(),
				h = this.checkHeight(nodes, height, type),
				w = this.checkWidth(nodes, width, type);

			if(nodes.length > 1){
				this._layoutIndicator.hide();
			}else{
				this._layoutIndicator.show();
			}


			for (var i = 0, iLen = nodes.length; i <  iLen; i++) {
				this.addHeightWidthToNode(nodes[i], h, w, e);
			}
		},

		/**
		 * returns the parent layout or an empty object.
		 */
		_getParentView: function(node) {
			var parentView = (node._layout && node._layout.parentLayout) || {};
			return parentView;
		},

		checkHeight: function(nodes, height, type) {
			if (height) {

				var topNode, parentView;
				// If we are resizing to the top, make sure we don't push the widget out of screen
				if (type.indexOf('t') !== -1) {
					// get the top most node
					nodes.sort(function(a, b) {
						return a.offsetTop - b.offsetTop;
					});
					topNode = nodes[0];

					parentView = this._getParentView(topNode);
					const padding = parentView.getMinimumTop ? parentView.getMinimumTop() : 0;
					height = Math.min(topNode._oldValues.top - padding, height);
				}

				// If we are resizing to the bottom, make sure we don't push the widget beyond the maximum height
				if (type.indexOf('b') !== -1) {
					// get the bottom most node
					nodes.sort(function(a, b) {
						return a.offsetTop + a._oldValues.height - b.offsetTop + b._oldValues.height;
					});
					topNode = nodes[0];
					parentView = this._getParentView(topNode);
					var maxHeight = parentView.getMaximumHeight ? parentView.getMaximumHeight() : Infinity;
					var topNodeHeightWithTop = topNode.offsetTop + topNode._oldValues.height;
					if (topNodeHeightWithTop + height > maxHeight) {
						height = maxHeight - topNodeHeightWithTop;
					}
				}

				if (height < 0) {
					// make sure we are not resizing smaller than the smallest allowed size
					nodes.sort(function(a, b) {
						return a._oldValues.height - b._oldValues.height;
					});
					var node = nodes[0];
					let minHeightPixel = WIDGET_SIZES.default.minHeight;
					if (node._layout && node._layout.widgetRegistry) {
						minHeightPixel = (WIDGET_SIZES[node._layout.widgetRegistry.widget] ? WIDGET_SIZES[node._layout.widgetRegistry.widget].minHeight : WIDGET_SIZES.default.minHeight);
					}
					height = -1 * Math.min(Math.abs(height), node._oldValues.height - minHeightPixel);
				}
			}
			return height;
		},

		checkWidth: function(nodes, width, type) {
			if (width) {

				var leftNode, parentView;
				//If we are resizing to the left, make sure we don't push the widget out of screen
				if (type.indexOf('l') !== -1) {
					// get the top most node
					nodes.sort(function(a, b) {
						return a.offsetLeft - b.offsetLeft;
					});
					leftNode = nodes[0];

					parentView = this._getParentView(leftNode);
					const padding = parentView.getMinimumLeft ? parentView.getMinimumLeft() : 0;
					width = Math.min(leftNode._oldValues.left - padding, width);
				}

				// If we are resizing to the right, make sure we don't push the widget beyond the maximum width
				if (type.indexOf('r') !== -1) {
					// get the bottom most node
					nodes.sort(function(a, b) {
						return a.offsetLeft + a._oldValues.width - b.offsetLeft + b._oldValues.width;
					});
					leftNode = nodes[0];
					parentView = this._getParentView(leftNode);
					var maxWidth = parentView.getMaximumWidth ? parentView.getMaximumWidth() : Infinity;
					var leftNodeWidthWithLeft = leftNode.offsetLeft + leftNode._oldValues.width;
					if (leftNodeWidthWithLeft + width >= maxWidth) {
						width = maxWidth - leftNodeWidthWithLeft;
					}

				}

				if (width < 0) {
					// make sure we are not resizing smaller than the smallest allowed size
					nodes.sort(function(a, b) {
						return a._oldValues.width - b._oldValues.width;
					});
					var node = nodes[0];
					let minWidthPixel = WIDGET_SIZES.default.minWidth;
					if (node._layout && node._layout.widgetRegistry) {
						minWidthPixel = (WIDGET_SIZES[node._layout.widgetRegistry.widget] ? WIDGET_SIZES[node._layout.widgetRegistry.widget].minWidth : WIDGET_SIZES.default.minWidth);
					}
					width = -1 * Math.min(Math.abs(width), node._oldValues.width - minWidthPixel);
				}
			}
			return width;
		},

		/**
		 * Add height and width and adjust the top and left based on the maintainBottom and maintainRight flags
		 *
		 * @param height
		 * @param width
		 */
		addHeightWidthToNode: function(n, height, width, e) {
			let displayValues = {};
			let newWidth = null;

			if (!isNaN(width)) {
				newWidth = n._oldValues.width + width;
			}
			let newHeight = null;
			if (!isNaN(height)) {
				newHeight = n._oldValues.height + height;
			}
			if (newHeight || newWidth) {
				let pos1 = this.getOppositeCornerCoords(n);
				this.updateSizeValue(n, 'height', newHeight);
				this.updateSizeValue(n, 'width', newWidth);
				displayValues = this._formatHeightWidth(n);
				this.updatePositionIndicator({ 'left' : e.clientX - this.containerNodeOffset.left, 'top' : e.clientY - this.containerNodeOffset.top }, displayValues);
				let pos2 = this.getOppositeCornerCoords(n);
				this._reAlignNode(n, pos1, pos2);
				this._showResizeIndicators(n, displayValues);
			}

			// If the layout has onResizeStep (e.g. groups), we continously notify the view to resize the content
			if (n._layout && n._layout.onResizeStep) {
				n._layout.onResizeStep();
			}
		},

		_formatHeightWidth: function(node) {
			var dragBox = {};

			if(node.id){
				const containerView = this._getParentGenericPage(node.id);
				const layoutPositioning  = node._layout.model.getValueFromSelfOrParent('layoutPositioning');

				if(layoutPositioning === 'relative'){
					let dragBoxModel = { height: node.getBoundingClientRect().height + 'px', width: node.getBoundingClientRect().width + 'px' };
					let pageSize = { width: containerView.$el.width(), height: containerView.$el.height() };
					PxPercentUtil.changePixelPropertiesToPercent(dragBoxModel, pageSize, 1);
					dragBox.height = Formatter.formatNumber(dragBoxModel.height.replace(/%/g, '')) + ' %';
					dragBox.width = Formatter.formatNumber(dragBoxModel.width.replace(/%/g, '')) + ' %';
				}else{
					dragBox.height = Formatter.formatNumber(String(Number(Math.round(node.getBoundingClientRect().height)))) + ' ' + stringResources.get('pixelUnit');
					dragBox.width = Formatter.formatNumber(String(Number(Math.round(node.getBoundingClientRect().width)))) + ' ' + stringResources.get('pixelUnit');
				}
			}
			return dragBox;
		},

		updateSizeValue: function(n, name, newValue) {
			if (newValue) {
				n.style[name] = newValue + 'px';
			}
		},


		/**
		 * Helper function that realign a node that realigns the node with its previous position.
		 *
		 * When we resize/ rotate, the element position wil shift. This method is used to restore the position.
		 * @param n
		 * @param pos1
		 * @param pos2
		 */
		_reAlignNode: function(n, pos1, pos2) {
			if (!$(n).is(ABSOLUTE_LAYOUT_CONTENT_SELECTORS)) {
				return;
			}

			var topLeft = utils.position(n);
			if (pos2.y !== pos1.y) {
				n.style.top = topLeft.top - pos2.y + pos1.y + 'px';
			}

			if (pos2.x !== pos1.x) {
				n.style.left = topLeft.left - pos2.x + pos1.x + 'px';
			}

		},


		/**
		 * Scale up/down
		 *
		 * @param e
		 */
		scale: function(e, type) {
			var axisNode = this.nResizeEdge.parentNode;
			var topLeft = utils.position(axisNode);
			var edgeCenterCoords = utils.getRotatedCoordinates(axisNode, this.resizePointCenter.left, this.resizePointCenter.top);
			var resizeCoords = utils.getRotatedCoordinates(axisNode, e.clientX, e.clientY);
			var parentLayout = this._getParentLayout();
			if (parentLayout && parentLayout.domNode !== this._guidelineManager.domNode) {
				var ids = this._getNodesForResize().map(
					function(n) {
						return n.id;
					}
				);
				this._guidelineManager.getReady(parentLayout.domNode, {
					dragBox: {
						ids: ids
					}
				});
			}
			this._guidelineManager.hideAll();

			var dragBox = { 'top': (topLeft.top + resizeCoords.y ), 'left': (topLeft.left + resizeCoords.x), 'bottom':  (topLeft.top + resizeCoords.y ),'right': (topLeft.left + resizeCoords.x) };
			var revisedCoordinate = this._guidelineManager.getSnapCoordinates(dragBox);

			var yOffset = resizeCoords.y - edgeCenterCoords.y - revisedCoordinate.top;
			if (type.indexOf('t') !== -1) {
				yOffset = -1 * yOffset;
			}

			var xOffset = resizeCoords.x - edgeCenterCoords.x - revisedCoordinate.left;
			if (type.indexOf('l') !== -1) {
				xOffset = -1 * xOffset;
			}

			this.applyScale(yOffset, xOffset, type, e);
		},
		/**
		 * Calculate the ratio of the scale up/down based on the the offset and set the nodes height/width
		 *
		 * @param offsetHeight
		 * @param offsetWidth
		 * @param type
		 */
		applyScale: function(offsetHeight, offsetWidth, type, e) {
			let nodes = this._getNodesForResize();

			if (offsetHeight) {
				// If we are resizing to the top, make sure we don't push the widget out of screen
				offsetHeight = this.checkHeight(nodes, offsetHeight, type);
			}

			if (offsetWidth) {
				//If we are resizing to the left, make sure we don't push the widget out of screen
				offsetWidth = this.checkWidth(nodes, offsetWidth, type);
			}

			let n;
			for (let i = 0, iLen = nodes.length; i < iLen; i++) {
				n = nodes[i];
				const h = n._oldValues.height;
				const w = n._oldValues.width;
				let ratio = 1;
				let parentView = this._getParentView(n);
				if ((offsetHeight && h < w) || !offsetWidth) {
					ratio = (h + offsetHeight) / h;

					// make sure width is acceptable, if not, calculate new ratio based on width
					let proposedWidth = w * ratio;
					let maxParentWidth = parentView.getMaximumWidth ? parentView.getMaximumWidth() : Infinity;
					let nodeWidthWithLeft = n.offsetLeft + proposedWidth;

					if(nodeWidthWithLeft > maxParentWidth) {
						ratio = (w + offsetWidth) / w;
					}
				} else {
					ratio = (w + offsetWidth) / w;

					// make sure height is within max allowable, else calculate new ratio based on height
					let proposedHeight = h * ratio;

					let maxParentHeight = parentView.getMaximumHeight ? parentView.getMaximumHeight() : Infinity;
					let nodeHeightWithTop = n.offsetTop + proposedHeight;

					if(nodeHeightWithTop > maxParentHeight) {
						ratio = (h + offsetHeight) / h;
					}
				}

				if (ratio > 0) {
					let pos1 = this.getOppositeCornerCoords(n);

					n.style.height = Math.round(h * ratio) + 'px';
					n.style.width = Math.round(w * ratio) + 'px';

					let pos2 = this.getOppositeCornerCoords(n);
					this._reAlignNode(n, pos1, pos2);

					// If the layout has onResizeStep (e.g. groups), we continuously notify the view to resize the content
					if (n._layout && n._layout.onResizeStep) {
						n._layout.onResizeStep();
					}

				}
				this._layoutIndicator.show();
				let positionDragBox = { 'height':Math.round(h * ratio), 'width': Math.round(w * ratio) };
				positionDragBox = this._formatHeightWidth(n, positionDragBox);
				this.updatePositionIndicator({ 'left' : e.clientX - this.containerNodeOffset.left, 'top' : e.clientY - this.containerNodeOffset.top }, positionDragBox);

				this._showResizeIndicators(n, positionDragBox);
			}
		},

		/**
		 * Rotate the node
		 *
		 * @param e
		 */
		rotate: function(e) {
			var n = this.nResizeEdge.parentNode;
			var center = utils.getCenter(n);
			var rotatedCenter = utils.getRotatedCoordinates(n.parentNode, center.left, center.top);
			var coords = utils.getRotatedCoordinates(n.parentNode, e.clientX, e.clientY);
			var rotateEdgeCoords = utils.getRotatedCoordinates(n.parentNode, this.resizePointCenter.left, this.resizePointCenter.top);
			var shiftPressed = e.shiftKey;
			this.rotateUsingCoords(rotatedCenter, coords, rotateEdgeCoords, shiftPressed, e);
		},

		move:function() {},

		/**
		 *
		 * @param center - coordinate of the center of the node
		 * @param coords - coordinates of the mouse
		 * @param rotateEdgeCoords - coordinates of the edge used to do the rotation
		 */
		rotateUsingCoords: function(center, coords, rotateEdgeCoords, snapTo45, e) {
			var n = this.nResizeEdge.parentNode;
			var nodes = this._getNodesForResize();
			for (var i = 0, iLen = nodes.length; i < iLen; i++) {
				n = nodes[i];
				var xx = center.x - coords.x;
				var yy = center.y - coords.y;
				var a = -1 * (Math.atan2(xx, yy) * (180 / Math.PI));

				var edgeAngle = 0;
				if (rotateEdgeCoords) {
					edgeAngle = -1 * (Math.atan2(center.x - rotateEdgeCoords.x, center.y - rotateEdgeCoords.y) * (180 / Math.PI)) - this.nodeAngle;
				}

				var offset = a - this.nodeAngle - edgeAngle;
				var angle = n._oldValues.rotateAngle + offset;
				if(snapTo45) {
					angle = Math.round(angle/45)*45;
				}

				if(nodes.length > 1){
					this._layoutIndicator.hide();
				}else{
					this._layoutIndicator.show();
					this.updatePositionIndicatorRotate({ 'left' : e.clientX - this.containerNodeOffset.left, 'top' : e.clientY - this.containerNodeOffset.top },angle);
				}
				utils.setRotateTransform(n, angle);
			}
		},

		rotationMutationObserver: function(mutations) {
			var styleMutated = false, i;
			for (i=0; i<mutations.length; i++) { //Check for a style mutation, so that we don't infinite loop on updating the class
				if (mutations[i].type==='attributes' && mutations[i].attributeName==='style') {
					styleMutated = true;
					break;
				}
			}
			if (styleMutated) {
				if (this._observerUpdateDelay) {
					window.clearTimeout(this._observerUpdateDelay);
				}
				this._observerUpdateDelay = window.setTimeout(function() {
					if (this.selectedNodes) {
						this.removeRotationClass(this.selectedNodes);
						for (i=0; i < this.selectedNodes.length; i++) {
							this.addRotationClass(this.selectedNodes[i]);
						}
					}
				}.bind(this), 200);
			}
		},

		addRotationClass: function(node) {
			var $n = $(node), nodeAngle = 0, $currentNode = $n;

			do { //Get the node rotation, including the rotation of containing groups.
				nodeAngle += utils.getAngleDegree($currentNode[0]);
				$currentNode = $currentNode.parent();
			} while ($currentNode.length && $currentNode.hasClass('pagegroup'));

			//There are 8 sections based on 45 degree arcs starting centered around 0 to change the mouse cursors based on rotation. Cursors use lowAngle <= actual < highAngle for the group boundries. From this we only need 4 classes, as 180 degree rotations share identical cursors.
			nodeAngle = Math.floor(((nodeAngle / 22.5) % 16) + 16) % 16; //Get a range from 0 - 15... to describe the current rotation.
			nodeAngle = Math.round(nodeAngle / 2) % 4; //Get a single integer representing the 45 degree arcs with 0 representing no rotation. (0-3 range), 180 degree mirrors share the same cursors
			$n.addClass('curRotation'+nodeAngle);
		},

		removeRotationClass: function(node) {
			$(node).removeClass('curRotation0 curRotation1 curRotation2 curRotation3');
		},

		resizeByProperty: function(style, value, units) {
			let sanitizedValue = value;
			let minPixel = null;

			// check if the id of the element being resized matches in the hash
			if (this.propertyNodes[0]._layout && this.propertyNodes[0]._layout.widgetRegistry && WIDGET_SIZES[this.propertyNodes[0]._layout.widgetRegistry.widget]) {
				// if style is width, use minWidth, else use minHeight
				minPixel = style === 'width' ? WIDGET_SIZES[this.propertyNodes[0]._layout.widgetRegistry.widget].minWidth : WIDGET_SIZES[this.propertyNodes[0]._layout.widgetRegistry.widget].minHeight;
			} else {
				// the resize element id did not match, so use the default min values
				minPixel = style === 'width' ? WIDGET_SIZES.default.minWidth : WIDGET_SIZES.default.minHeight;
			}

			let parentSize = this._getParentDim(style, this.propertyNodes);
			let offset = this._getWidgetOffset(style, this.propertyNodes);
			if (units === 'px') {
				sanitizedValue = Math.max(minPixel, sanitizedValue);
				sanitizedValue = Math.min(parentSize - offset, sanitizedValue);
			} else { //% size
				sanitizedValue = Math.max(sanitizedValue, (minPixel/parentSize)*100);

				const sizeProperty = style === 'width' ? 'left' : 'top';
				sanitizedValue = this._sanitizePercentSizeOnPage(sanitizedValue, sizeProperty);
			}
			const propertyValue = sanitizedValue + units;
			let sizeInfoArray = [];
			this.propertyNodes.forEach((node) => {
				let styleUpdate = {};
				styleUpdate[style] = propertyValue;
				sizeInfoArray.push({
					style: styleUpdate,
					id: node._layout.model.id
				});
			});

			this.updateModel(sizeInfoArray);

			return sanitizedValue;
		},

		rotateByProperty: function(value) {
			let sanitizedValue = ((value % 360) + 360) % 360;

			let rotationInfoArray = [];
			this.propertyNodes.forEach((node) => {
				if (!(this.preventRotate(node))) {
					rotationInfoArray.push({
						style: {
							'rotateAngle': sanitizedValue
						},
						id: node._layout.model.id
					});
				}
			});

			this.updateModel(rotationInfoArray);

			return sanitizedValue;
		},

		_processChange(id, numericValue, units) {
			if (id === 'rotation') {
				return this.rotateByProperty(numericValue);
			} else {
				return this.resizeByProperty(id==='width' ? 'width' : 'height', numericValue, units);
			}
		},

		/* TODO: This has been replaced by API work. Remove the unused functions, and the base class if no longer needed. */
		getProperties: function() {
			Resize.inherited('getProperties', this, arguments);
			const resizeUnit = this.getUnitsFromValue(this.propertyNodes[0]._layout.model.style.width);
			const rotateUnit = '\xB0';
			let suppressRotate = this.preventRotate(this.propertyNodes[0]); //Don't show the rotate property if all selected widgets are non-rotatable.
			let propValues = [{
				units: resizeUnit,
				getProp: (node) => node._layout.model.style.width
			}, {
				units: resizeUnit,
				getProp: (node) => node._layout.model.style.height
			}, {
				units: rotateUnit,
				getProp: (node) => (utils.getAngleDegree(node) + 360) % 360
			}];
			if (suppressRotate) {
				propValues.push({
					units: '',
					getProp: this.preventRotate
				});
			}
			this._getValuesForProperties(propValues);

			if (suppressRotate) {
				suppressRotate = propValues[3].value==='' ? false : true;
			}

			let properties = [{
				'type': 'SectionLabel',
				'label': stringResources.get('propResizeLabel'),
				'tabName': stringResources.get('tabName_general'),
				'sectionName': stringResources.get('sectionName_layout'),
				'sectionOpened': true,
				order: 30
			}, {
				'type': 'Split',
				'name': 'resizeWidgetSplit',
				'id': 'resizeWidgetSplit',
				'tabName': stringResources.get('tabName_general'),
				'sectionName': stringResources.get('sectionName_layout'),
				order: 31,
				'items': [{
					align: 'left',
					items: [{
						'type': 'InputLabel',
						'label': stringResources.get('propResizeWidth_v1'),
						'name': 'width',
						'id': 'width',
						'tabName': stringResources.get('tabName_general'),
						'sectionName': stringResources.get('sectionName_layout'),
						'readOnly': false,
						'value': propValues[0].value,
						'onChangeValueHold': propValues[0].value,
						'multiline': true,
						'handleReturnKey': true,
						'units': resizeUnit,
						'onChange': this.wrapChangeProperty()
					}]
				}, {
					align: 'right',
					items: [{
						'type': 'InputLabel',
						'label': stringResources.get('propResizeHeight_v1'),
						'name': 'height',
						'id': 'height',
						'tabName': stringResources.get('tabName_general'),
						'sectionName': stringResources.get('sectionName_layout'),
						'readOnly': false,
						'value': propValues[1].value,
						'onChangeValueHold': propValues[1].value,
						'multiline': true,
						'handleReturnKey': true,
						'units': resizeUnit,
						'onChange': this.wrapChangeProperty()
					}]
				}]
			}];

			if (!suppressRotate) {
				properties.push({
					'type': 'SectionLabel',
					'label': this.isPropertiesV2Enabled ? stringResources.get('propRotateLabel') : stringResources.get('propRotateLabel_v1'),
					'tabName': stringResources.get('tabName_general'),
					'sectionName': stringResources.get('sectionName_layout'),
					'order': 32
				});
				properties.push({
					'type': 'InputLabel',
					'label': stringResources.get('propRotateAngle'),
					'name': 'rotation',
					'id': 'rotation',
					'tabName': stringResources.get('tabName_general'),
					'sectionName': stringResources.get('sectionName_layout'),
					'readOnly': false,
					'value': propValues[2].value,
					'onChangeValueHold': propValues[2].value,
					'handleReturnKey': true,
					'units': rotateUnit,
					'onChange': this.wrapChangeProperty(),
					'order': 33
				});
			}

			if (this.showStyleProperties(this.propertyNodes[0])) {
				return properties;
			} else {
				return [];
			}
		}
	});

	return Resize;
});
